import { Inject, Injectable } from '@angular/core';
import {
  CultureInfoService,
  Event,
  ExportToken,
  HttpClientService,
  LogService,
  TOKENS as COMMON_TOKENS,
} from '@trustedshops/tswp-core-common';
import { toPromise } from '@trustedshops/tswp-core-common-eventing-rxjs';
import {
  InvalidNotificationReceiverError,
  NotificationChannel,
  NotificationReceiverType,
  NotificationReceiverTypeValue,
  NotificationSettingsConfig,
  NotificationSettingsService,
  NotificationsAPIResponse,
  TOKENS as NOTIFICATIONS_TOKENS,
} from '@trustedshops/tswp-core-notifications';
import { RemovalBody } from '../models/removal-body.interface';

/** Shape of the `check-registration` response this service reads. */
interface RegistrationResponse {
  registered: boolean;
}

@Injectable()
@ExportToken(NOTIFICATIONS_TOKENS.NotificationSettingsService)
export class NotificationSettingsServiceImpl
  implements NotificationSettingsService
{
  private static readonly TYPE = 'NotificationSettingsService';

  /**
   * Every subscription covers both review products. The notification API has
   * no concept of subscribing to one and not the other.
   */
  private static readonly PRODUCT_NAMES = [
    'PRODUCT_REVIEWS',
    'SERVICE_REVIEWS',
  ];

  /**
   * A rating filter is only a filter while it selects some ratings and not
   * all of them. Both an empty filter and one holding every rating write
   * `enabled: false`, which the API stores as an unsubscribe.
   */
  private static readonly RATING_COUNT = 6;

  /**
   * Matches an email address.
   *
   * Deliberately unanchored, as the notification API has always accepted any
   * identifier containing a well-formed address. Anchoring it would reject
   * identifiers that are stored and reachable today.
   *
   * Source: https://stackoverflow.com/a/201378
   */
  /* eslint-disable no-control-regex -- the quoted-local-part branch matches the
     control characters RFC 5322 permits inside a quoted string. */
  private static readonly EMAIL_PATTERN =
    /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;

  /* eslint-enable no-control-regex */

  /** Matches the `idn-<uuid>` identity reference a web receiver carries. */
  private static readonly WEB_PATTERN =
    /(idn-[a-zA-Z0-9]{8}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{12})/;

  private readonly baseUrl: string;

  /**
   * Subscription records already read in this session, keyed by receiver.
   *
   * A write invalidates the receiver's entry rather than replacing it, because
   * a write body carries only the channels that write touched. Storing the
   * body would leave a partial record behind that a later read would trust.
   */
  private readonly subscriptions = new Map<string, NotificationsAPIResponse>();

  /**
   * Whether the API already knows the receiver, keyed by receiver type.
   *
   * A user holds one receiver per type, so the type identifies it. The flag
   * decides whether a write registers or updates, and it is only populated by
   * `checkAndSynchClientStatus`; an unread type registers.
   */
  private readonly isExistingClient = new Map<
    NotificationReceiverTypeValue,
    boolean
  >();

  public constructor(
    @Inject(COMMON_TOKENS.HttpClientService)
    private readonly _httpClient: HttpClientService,
    @Inject(COMMON_TOKENS.LogService)
    private readonly _logService: LogService,
    @Inject(COMMON_TOKENS.CultureInfoService)
    private readonly _cultureInfoService: CultureInfoService,
    @Inject(NOTIFICATIONS_TOKENS.NotificationSettingsServiceConfig)
    config: NotificationSettingsConfig
  ) {
    // The configured base path may or may not carry a trailing slash. Strip it
    // once here so every request path can join with a single slash.
    this.baseUrl = config.basePath.replace(/\/+$/, '');
  }

  public async checkAndSynchClientStatus(
    channelList: string[],
    receiverId: string,
    receiverType: NotificationReceiverTypeValue
  ): Promise<number[]> {
    const registered = await this.checkClientRegistration(receiverId);
    this.isExistingClient.set(receiverType, registered);

    if (!registered) {
      this._logService.information(
        NotificationSettingsServiceImpl.TYPE,
        'Receiver is not registered. Returning an empty rating filter.'
      );
      return [];
    }

    const subscription = await this.getSubscribedChannels(receiverId);
    const products = subscription.channels.flatMap(
      (channel) => channel.products
    );

    // A receiver counts as subscribed only while every product is enabled. A
    // mixed state reads as unsubscribed, so the caller shows every rating off.
    if (
      products.length === 0 ||
      !products.every((product) => product.enabled)
    ) {
      return [];
    }

    const ratingFilter = products[0].properties.ratingFilter;
    await this.synchChannels(
      subscription,
      receiverId,
      receiverType,
      channelList,
      ratingFilter
    );

    return ratingFilter;
  }

  public async updateOrCreateClient(
    receiverId: string,
    receiverType: NotificationReceiverTypeValue,
    channelList: string[],
    ratingFilter: number[]
  ): Promise<void> {
    if (!this.isValidReceiver(receiverId, receiverType)) {
      throw this._logService.error(
        NotificationSettingsServiceImpl.TYPE,
        new InvalidNotificationReceiverError(receiverType)
      );
    }

    const body = this.buildRequestBody(
      receiverId,
      receiverType,
      channelList,
      ratingFilter
    );

    if (this.isExistingClient.get(receiverType)) {
      await this.updateChannels(receiverId, body);
      return;
    }

    await this.registerClient(body);
  }

  /**
   * Brings the API's channel set in line with the channels the receiver can
   * currently see, adding the missing ones and removing the stale ones.
   */
  private async synchChannels(
    subscription: NotificationsAPIResponse,
    receiverId: string,
    receiverType: NotificationReceiverTypeValue,
    channelList: string[],
    ratingFilter: number[]
  ): Promise<void> {
    const knownChannels = subscription.channels.map(
      (channel) => channel.channelRef
    );

    const addedChannels = channelList.filter(
      (channel) => !knownChannels.includes(channel)
    );
    if (addedChannels.length > 0) {
      const body = this.buildRequestBody(
        receiverId,
        receiverType,
        addedChannels,
        ratingFilter
      );
      await this.updateChannels(receiverId, body);
      // Keep the in-flight record whole, so the removal step below compares
      // against the full channel set rather than the pre-addition one.
      subscription.channels.push(...body.channels);
    }

    const removedChannels = knownChannels.filter(
      (channel) => !channelList.includes(channel)
    );
    if (removedChannels.length > 0) {
      await this.removeChannels(receiverId, removedChannels);
    }
  }

  private buildRequestBody(
    receiverId: string,
    receiverType: NotificationReceiverTypeValue,
    channelList: string[],
    ratingFilter: number[]
  ): NotificationsAPIResponse {
    const enabled =
      ratingFilter.length > 0 &&
      ratingFilter.length < NotificationSettingsServiceImpl.RATING_COUNT;

    const channels: NotificationChannel[] = channelList.map((channelRef) => ({
      channelRef,
      products: NotificationSettingsServiceImpl.PRODUCT_NAMES.map(
        (productName) => ({
          productName,
          enabled,
          properties: { ratingFilter },
        })
      ),
    }));

    return {
      notificationType: receiverType,
      receiverId,
      channels,
      eventDate: this.currentEventDate(),
      clientLocale:
        this._cultureInfoService.currentCulture.value.ietfLanguageTag,
    };
  }

  /**
   * The current local time, to the second, as an ISO 8601 timestamp with a UTC
   * offset — for example `2026-09-07T14:34:56+02:00`.
   *
   * The notification API has received this shape since the feature shipped, so
   * it is reproduced here rather than replaced with a UTC `Z` timestamp.
   */
  private currentEventDate(): string {
    const now = new Date();
    const pad = (value: number): string => String(value).padStart(2, '0');

    const offsetMinutes = -now.getTimezoneOffset();
    const offsetSign = offsetMinutes < 0 ? '-' : '+';
    const offsetHours = pad(Math.floor(Math.abs(offsetMinutes) / 60));
    const offsetRemainder = pad(Math.abs(offsetMinutes) % 60);

    const date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
      now.getDate()
    )}`;
    const time = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(
      now.getSeconds()
    )}`;

    return `${date}T${time}${offsetSign}${offsetHours}:${offsetRemainder}`;
  }

  private isValidReceiver(
    receiverId: string,
    receiverType: NotificationReceiverTypeValue
  ): boolean {
    if (receiverType === NotificationReceiverType.Email) {
      return NotificationSettingsServiceImpl.EMAIL_PATTERN.test(receiverId);
    }

    if (receiverType === NotificationReceiverType.Web) {
      return NotificationSettingsServiceImpl.WEB_PATTERN.test(receiverId);
    }

    return false;
  }

  //#region Notification API calls

  private async checkClientRegistration(receiverId: string): Promise<boolean> {
    const response = await this.send<RegistrationResponse>('get', () =>
      this._httpClient.get<RegistrationResponse>(
        `${this.baseUrl}/clients/${receiverId}/check-registration`,
        {}
      )
    );

    return response.registered;
  }

  private async getSubscribedChannels(
    receiverId: string
  ): Promise<NotificationsAPIResponse> {
    const cached = this.subscriptions.get(receiverId);
    if (cached) {
      return cached;
    }

    const subscription = await this.send<NotificationsAPIResponse>('get', () =>
      this._httpClient.get<NotificationsAPIResponse>(
        `${this.baseUrl}/clients/${receiverId}`,
        {}
      )
    );

    this.subscriptions.set(receiverId, subscription);
    return subscription;
  }

  /** Registers a receiver the API does not know yet. The path is singular. */
  private async registerClient(body: NotificationsAPIResponse): Promise<void> {
    await this.send<unknown>('post', () =>
      this._httpClient.post<unknown>(`${this.baseUrl}/client`, body, {})
    );
    this.subscriptions.delete(body.receiverId);
  }

  private async updateChannels(
    receiverId: string,
    body: NotificationsAPIResponse
  ): Promise<void> {
    await this.send<unknown>('put', () =>
      this._httpClient.put<unknown>(
        `${this.baseUrl}/clients/${receiverId}`,
        body,
        {}
      )
    );
    // The body holds only the channels this write touched, so it is not a
    // usable cache entry. Drop the entry and let the next read re-fetch.
    this.subscriptions.delete(receiverId);
  }

  /** Removes channels. The endpoint is a POST, not a DELETE. */
  private async removeChannels(
    receiverId: string,
    channelRefs: string[]
  ): Promise<void> {
    const body: RemovalBody = {
      channels: channelRefs.map((channelRef) => ({
        channelRef,
        productNames: [...NotificationSettingsServiceImpl.PRODUCT_NAMES],
      })),
    };

    await this.send<unknown>('post', () =>
      this._httpClient.post<unknown>(
        `${this.baseUrl}/clients/${receiverId}/channels`,
        body,
        {}
      )
    );
    this.subscriptions.delete(receiverId);
  }

  /**
   * Issues one request and logs a failure before re-throwing it.
   *
   * The carrier registers the notification API as a protected endpoint, so the
   * platform HTTP client attaches the bearer token. This service never builds
   * an authorization header.
   */
  private async send<T>(method: string, request: () => Event<T>): Promise<T> {
    try {
      return await toPromise(request());
    } catch (error) {
      throw this._logService.error(
        NotificationSettingsServiceImpl.TYPE,
        error as Error,
        { method }
      );
    }
  }

  //#endregion
}
